import { useCallback, useEffect, useRef } from "react";
import { getLocalJWT } from "../functions/utilities";
import { RestS } from "../../rest/rest-service";

const DEBOUNCE_MS = 500;

interface AutoSaveOptions {
  companyId: string;
  currentStep: string;
  storageKeyPrefix?: string;
}

interface SaveToDBOptions {
  keepalive?: boolean;
}

const DEFAULT_PREFIX = "uw";

interface LocalStorageEntry {
  data: Record<string, any>;
  timestamp: number;
  step: string;
}

function getStorageKey(
  companyId: string,
  prefix: string = DEFAULT_PREFIX,
): string {
  return `${prefix}_${companyId}`;
}

export function getLocalStorageDraft(
  companyId: string,
  prefix: string = DEFAULT_PREFIX,
): LocalStorageEntry | null {
  try {
    const raw = localStorage.getItem(getStorageKey(companyId, prefix));
    if (!raw) return null;
    return JSON.parse(raw) as LocalStorageEntry;
  } catch {
    return null;
  }
}

export function clearLocalStorageDraft(
  companyId: string,
  prefix: string = DEFAULT_PREFIX,
): void {
  try {
    localStorage.removeItem(getStorageKey(companyId, prefix));
  } catch {
    // silent fail
  }
}

export function useAutoSave({
  companyId,
  currentStep,
  storageKeyPrefix = DEFAULT_PREFIX,
}: AutoSaveOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentStepRef = useRef(currentStep);
  // Updated immediately on every saveToLocalStorage call (before debounce)
  // so beforeunload can read the most recent form values.
  const latestValuesRef = useRef<Record<string, any>>({});

  // Keep step ref in sync so the debounced callback always uses the latest step
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const saveToLocalStorage = useCallback(
    (values: Record<string, any>) => {
      if (!companyId) return;

      // Capture immediately (no debounce) so beforeunload can read latest values
      latestValuesRef.current = values;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        try {
          const entry: LocalStorageEntry = {
            data: values,
            timestamp: Date.now(),
            step: currentStepRef.current,
          };
          localStorage.setItem(
            getStorageKey(companyId, storageKeyPrefix),
            JSON.stringify(entry),
          );
        } catch {
          // localStorage full or unavailable - silent fail
        }
      }, DEBOUNCE_MS);
    },
    [companyId, storageKeyPrefix],
  );

  const saveToDB = useCallback(
    (formData: Record<string, any>, step: string, options?: SaveToDBOptions) => {
      if (!companyId) return;

      const payload = {
        data: formData,
        step: step,
      };

      if (options?.keepalive) {
        try {
          const token = getLocalJWT();
          const baseUrl = import.meta.env.VITE_JIGSAW_DEV_REST_LOCATION;
          const body = JSON.stringify(payload);

          // keepalive has 64KB body limit — skip if exceeded, localStorage backup handles recovery
          if (body.length > 65536) return;

          fetch(`${baseUrl}underwriting`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body,
            keepalive: true,
          });
        } catch {
          // silent fail — localStorage backup handles recovery
        }
      } else {
        RestS.submitUnderwritingFormData(payload).catch(() => {
          // silent fail
        });
      }
    },
    [companyId],
  );

  /** Immediately write to localStorage — no debounce. Use before navigation / unmount. */
  const flushToLocalStorage = useCallback(
    (values: Record<string, any>) => {
      if (!companyId) return;

      // Cancel any pending debounced write
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      try {
        const entry: LocalStorageEntry = {
          data: values,
          timestamp: Date.now(),
          step: currentStepRef.current,
        };
        localStorage.setItem(
          getStorageKey(companyId, storageKeyPrefix),
          JSON.stringify(entry),
        );
      } catch {
        // localStorage full or unavailable - silent fail
      }
    },
    [companyId, storageKeyPrefix],
  );

  /** Returns the most recent values captured by saveToLocalStorage (no debounce delay). */
  const getLatestValues = useCallback(() => latestValuesRef.current, []);

  return { saveToLocalStorage, flushToLocalStorage, saveToDB, getLatestValues };
}
